import UnsavedChangesBoundary, { DirtyState } from "./components/common/UnsavedChangesBoundary";

import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import { getRiskCategories } from "./riskService";
import { createRiskCategory } from "./api/riskCategories.api";
import { getRiskRatingDefinitions } from "./api/riskRatingDefinitions.api";
import { getRiskMatrixConfig } from "./api/riskMatrixConfig.api";
import { getImpactThemeLevels, listImpactThemes } from "./api/impactThemes.api";
import { listAdminUsers } from "./api/admin.api";
import { useAuth } from "./useAuth";
import { RoleEnum, isRole } from "./constants/roles";
import FormWithValidation from "./components/common/FormWithValidation";
import { useToast } from "./components/common/ToastProvider";
import RiskMatrixSelector from "./components/risks/RiskMatrixSelector";
import useAddRiskForm from "./hooks/useAddRiskForm";
import Button from "./ui/Button";
import Input from "./ui/Input";
import Select from "./ui/Select";
import ModalA11yWrapper from "./ui/ModalA11yWrapper";
import RichTextEditor from "./ui/RichTextEditor";
import CollapsibleSection from "./ui/CollapsibleSection";
import { spacing, typography } from "./ui/tokens";
import {
  buildRiskCategoryPayload,
  getRiskCategoryNameError,
  normalizeRiskCategoryName,
  RISK_CATEGORY_NAME_MAX_LENGTH,
} from "./utils/riskCategoryForm";
import { getCellScore, normalizeMatrixConfig as normalizeSharedMatrixConfig, scoreToBand } from "./utils/riskMatrixLogic";

const ADD_NEW_CATEGORY_OPTION_VALUE = "__add_new_category__";

const toOptionalNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const normalizeDefinitionMap = (entries) => {
  const map = new Map();
  (entries || []).forEach((item) => {
    const level = Number(item?.level);
    if (!Number.isFinite(level)) return;
    map.set(level, {
      level,
      label: String(item?.label || "").trim(),
      definition: String(item?.definition || "").trim(),
    });
  });
  return map;
};

const normalizeMatrixConfig = (payload) => normalizeSharedMatrixConfig(payload);

const toOptionalId = (value) => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export default function AddRiskForm({ onClose, onSaved }) {
  const { user } = useAuth();
  const { success: showSuccessToast } = useToast();
  const titleId = useId();
  const isAdmin = isRole(user?.role, RoleEnum.ADMIN);
  const canCreateCategories = isAdmin;

  const {
    schema,
    defaultValues,
    formState,
    setters,
    loading,
    formError,
    serverFieldErrors,
    submitRisk,
  } = useAddRiskForm({ onClose, onSaved });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const [newCategoryError, setNewCategoryError] = useState("");
  const [creatingCategory, setCreatingCategory] = useState(false);

  const [ownerOptions, setOwnerOptions] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [ownersError, setOwnersError] = useState(null);

  const [ratingDefinitions, setRatingDefinitions] = useState(null);
  const [ratingDefinitionsLoading, setRatingDefinitionsLoading] = useState(true);
  const [ratingDefinitionsError, setRatingDefinitionsError] = useState(null);

  const [impactThemes, setImpactThemes] = useState([]);
  const [impactThemesLoading, setImpactThemesLoading] = useState(true);
  const [impactThemesError, setImpactThemesError] = useState(null);

  const [impactThemeLevels, setImpactThemeLevels] = useState([]);
  const [impactThemeLevelsLoading, setImpactThemeLevelsLoading] = useState(true);
  const [impactThemeLevelsError, setImpactThemeLevelsError] = useState(null);

  const [matrixConfig, setMatrixConfig] = useState(() => normalizeMatrixConfig(null));
  const [defaultMatrixId, setDefaultMatrixId] = useState(null);
  const [matrixConfigLoading, setMatrixConfigLoading] = useState(true);
  const [matrixConfigError, setMatrixConfigError] = useState(null);

  const selectedImpactThemeId = formState.impact_theme_id;
  const [initialImpactThemeId, setInitialImpactThemeId] = useState(null);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    setCategoriesError(null);
    try {
      const res = await getRiskCategories({ include_archived: true });
      const items = res?.data || res || [];
      setCategories(items);
      return items;
    } catch (err) {
      setCategories([]);
      setCategoriesError(err?.response?.data?.error || err?.message || "Unable to load risk categories.");
      return [];
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const loadRatingDefinitions = useCallback(async () => {
    setRatingDefinitionsLoading(true);
    setRatingDefinitionsError(null);
    try {
      const res = await getRiskRatingDefinitions();
      const payload = res?.data || res;
      setRatingDefinitions(payload);
      return payload;
    } catch (err) {
      setRatingDefinitions(null);
      setRatingDefinitionsError(
        err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Unable to load rating definitions."
      );
      return null;
    } finally {
      setRatingDefinitionsLoading(false);
    }
  }, []);

  const loadMatrixConfig = useCallback(async () => {
    setMatrixConfigLoading(true);
    setMatrixConfigError(null);
    try {
      const res = await getRiskMatrixConfig();
      const payload = res?.data || res;
      setMatrixConfig(normalizeMatrixConfig(payload));
      setDefaultMatrixId(toOptionalId(payload?.matrix_id));
      return payload;
    } catch (err) {
      setMatrixConfig(normalizeMatrixConfig(null));
      setDefaultMatrixId(null);
      setMatrixConfigError(
        err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Unable to load matrix configuration."
      );
      return null;
    } finally {
      setMatrixConfigLoading(false);
    }
  }, []);
  const loadImpactThemes = useCallback(async () => {
    setImpactThemesLoading(true);
    setImpactThemesError(null);
    try {
      const res = await listImpactThemes();
      const payload = res?.data || res || [];
      const normalized = Array.isArray(payload) ? payload : [];
      setImpactThemes(normalized);
      const current = Number(selectedImpactThemeId);
      if (!current || !normalized.some((theme) => Number(theme?.id) === current)) {
        const fallback = normalized.find((theme) => theme?.is_default) || normalized[0] || null;
        const defaultThemeId = fallback?.id ? Number(fallback.id) : null;
        setInitialImpactThemeId(defaultThemeId);
        setters.setImpactThemeId(defaultThemeId);
      }
      return normalized;
    } catch (err) {
      setImpactThemes([]);
      setImpactThemesError(
        err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Unable to load impact themes."
      );
      setInitialImpactThemeId(null);
      setters.setImpactThemeId(null);
      return [];
    } finally {
      setImpactThemesLoading(false);
    }
  }, [selectedImpactThemeId, setters]);

  const loadImpactThemeLevels = useCallback(async (themeId) => {
    if (!themeId) {
      setImpactThemeLevels([]);
      setImpactThemeLevelsError(null);
      setImpactThemeLevelsLoading(false);
      return [];
    }

    setImpactThemeLevelsLoading(true);
    setImpactThemeLevelsError(null);
    try {
      const res = await getImpactThemeLevels(themeId);
      const payload = res?.data || res;
      const levels = payload?.levels || [];
      setImpactThemeLevels(levels);
      return levels;
    } catch (err) {
      setImpactThemeLevels([]);
      setImpactThemeLevelsError(
        err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Unable to load impact theme levels."
      );
      return [];
    } finally {
      setImpactThemeLevelsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
    loadRatingDefinitions();
    loadMatrixConfig();
  }, [loadCategories, loadMatrixConfig, loadRatingDefinitions]);

  useEffect(() => {
    loadImpactThemes();
  }, [loadImpactThemes]);

  useEffect(() => {
    loadImpactThemeLevels(selectedImpactThemeId);
  }, [selectedImpactThemeId, loadImpactThemeLevels]);

  useEffect(() => {
    let active = true;
    const loadOwners = async () => {
      setOwnersLoading(true);
      setOwnersError(null);
      try {
        const res = await listAdminUsers();
        const users = res?.data || [];
        const options = users
          .filter((entry) => entry?.username)
          .map((entry) => ({
            value: entry.username,
            label: entry.email ? `${entry.username} (${entry.email})` : entry.username,
          }));
        if (active) {
          setOwnerOptions(options);
        }
      } catch (err) {
        const fallback = user?.username
          ? [{ value: user.username, label: user.email ? `${user.username} (${user.email})` : user.username }]
          : [];
        if (active) {
          setOwnerOptions(fallback);
          setOwnersError(err?.response?.data?.detail || err?.message || "Unable to load users.");
        }
      } finally {
        if (active) {
          setOwnersLoading(false);
        }
      }
    };

    loadOwners();
    return () => {
      active = false;
    };
  }, [user?.username, user?.email]);

  useEffect(() => {
    const handleDefinitionsChanged = () => {
      loadRatingDefinitions();
    };
    const handleMatrixChanged = () => {
      loadMatrixConfig();
    };
    const handleThemesChanged = () => {
      loadImpactThemes();
    };

    window.addEventListener("riskRatingDefinitionsChanged", handleDefinitionsChanged);
    window.addEventListener("riskMatrixConfigChanged", handleMatrixChanged);
    window.addEventListener("impactThemesChanged", handleThemesChanged);
    return () => {
      window.removeEventListener("riskRatingDefinitionsChanged", handleDefinitionsChanged);
      window.removeEventListener("riskMatrixConfigChanged", handleMatrixChanged);
      window.removeEventListener("impactThemesChanged", handleThemesChanged);
    };
  }, [loadImpactThemes, loadMatrixConfig, loadRatingDefinitions]);

  const activeCategories = useMemo(
    () => (categories || []).filter((entry) => (entry?.status || "active") === "active"),
    [categories]
  );
  const canSelectCategory = activeCategories.length > 0;

  const likelihoodDefinitionsByLevel = useMemo(
    () => normalizeDefinitionMap(ratingDefinitions?.likelihood),
    [ratingDefinitions]
  );
  const impactDefinitionsByLevel = useMemo(
    () => normalizeDefinitionMap(impactThemeLevels),
    [impactThemeLevels]
  );

  const selectedImpactTheme = useMemo(
    () => impactThemes.find((entry) => Number(entry?.id) === Number(selectedImpactThemeId)) || null,
    [impactThemes, selectedImpactThemeId]
  );

  const handleCreateCategory = async (setValue) => {
    setNewCategoryError("");
    const validationError = getRiskCategoryNameError(newCategoryName, categories);
    if (validationError) {
      setNewCategoryError(validationError);
      return;
    }

    setCreatingCategory(true);
    try {
      const payload = buildRiskCategoryPayload({
        name: newCategoryName,
        description: newCategoryDescription,
        status: "active",
      });
      const createdRes = await createRiskCategory(payload);
      const createdCategory = createdRes?.data || createdRes;
      const selectedName = normalizeRiskCategoryName(createdCategory?.name || payload.name);

      setCategories((prev) => {
        const base = Array.isArray(prev) ? [...prev] : [];
        const existingIndex = base.findIndex((entry) => String(entry?.id) === String(createdCategory?.id));
        if (existingIndex >= 0) {
          base[existingIndex] = { ...base[existingIndex], ...createdCategory };
        } else {
          base.push(createdCategory);
        }
        return base.sort((a, b) =>
          normalizeRiskCategoryName(a?.name).localeCompare(normalizeRiskCategoryName(b?.name), undefined, {
            sensitivity: "base",
          })
        );
      });

      setTimeout(() => {
        setValue("category", selectedName, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
        setValue("subcategory", "", { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      }, 0);

      setNewCategoryName("");
      setNewCategoryDescription("");
      setShowCreateCategory(false);
      showSuccessToast?.("Category created successfully.");
      window.dispatchEvent(new CustomEvent("riskCategoriesChanged"));
      loadCategories();
    } catch (err) {
      const nameError = err?.response?.data?.field_errors?.name;
      setNewCategoryError(
        nameError ||
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          "Unable to create category."
      );
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleSubmit = async (values) => {
    await submitRisk({
      values,
      impactThemeId: selectedImpactThemeId,
      defaultMatrixId,
    });
  };

  return (
    <UnsavedChangesBoundary onClose={onClose} itemName="risk" closeEvent="closeAddRiskForm">
      {(requestClose) => (
    <ModalA11yWrapper open onClose={requestClose} labelledBy={titleId}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h2 id={titleId} style={{ margin: 0, fontSize: typography.size.xl }}>Add New Risk</h2>
          <Button onClick={requestClose} variant="ghost" style={closeStyle}>x</Button>
        </div>

        <DirtyState isDirty={Boolean(newCategoryName || newCategoryDescription) || selectedImpactThemeId !== initialImpactThemeId} />
        <FormWithValidation
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onCancel={requestClose}
          submitLabel={loading ? "Saving..." : "Save Risk"}
          submitDisabled={(state) => loading || !state.isValid}
          formError={formError}
          serverFieldErrors={serverFieldErrors}
        >
          {({ register, formState: validationState, watch, setValue }) => {
            const categoryRegister = register("category", {
              onChange: (event) => {
                const selectedValue = event?.target?.value || "";
                if (selectedValue === ADD_NEW_CATEGORY_OPTION_VALUE) {
                  setShowCreateCategory(true);
                  setNewCategoryError("");
                  setValue("category", "", { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                  setValue("subcategory", "", { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                  return;
                }
                if (showCreateCategory) {
                  setShowCreateCategory(false);
                  setNewCategoryError("");
                }
              },
            });

            const nameRegister = register("name", {
              onChange: (event) => setters.setName(event?.target?.value || ""),
            });
            const ownerRegister = register("owner_id", {
              onChange: (event) => setters.setOwnerId(event?.target?.value || ""),
            });
            const editorRegister = register("editor_id", {
              onChange: (event) => setters.setEditorId(event?.target?.value || ""),
            });

            const likelihoodRaw = watch("likelihood");
            const impactRaw = watch("impact");
            const likelihoodValue = Number.isInteger(Number(likelihoodRaw)) ? Number(likelihoodRaw) : null;
            const impactValue = Number.isInteger(Number(impactRaw)) ? Number(impactRaw) : null;
            const hasSelectedCell = Number.isInteger(likelihoodValue) && Number.isInteger(impactValue);
            const selectedScore = hasSelectedCell
              ? getCellScore({
                  scoringMode: matrixConfig?.scoring_mode,
                  cellScores: matrixConfig?.cell_scores,
                  lik: likelihoodValue,
                  imp: impactValue,
                })
              : null;
            const selectedBand = hasSelectedCell
              ? scoreToBand({ score: selectedScore, bandThresholds: matrixConfig?.band_thresholds })
              : null;
            const selectedLikelihoodDefinition = hasSelectedCell ? likelihoodDefinitionsByLevel.get(likelihoodValue) : null;
            const selectedImpactDefinition = hasSelectedCell ? impactDefinitionsByLevel.get(impactValue) : null;
            const selectedImpactThemeName = selectedImpactTheme?.name || "General";

            return (
              <div style={formStyle}>
                <div style={infoBannerStyle}>
                  Only the Risk Name is required. Other fields can be completed later.
                </div>

                {(categoriesLoading || categoriesError) && (
                  <div style={alertStyle} role="alert">
                    {categoriesLoading && "Loading risk categories..."}
                    {categoriesError && !categoriesLoading && categoriesError}
                  </div>
                )}

                <CategoryWatcher
                  categories={activeCategories}
                  setSubcategories={setSubcategories}
                  watch={watch}
                  setValue={setValue}
                />

                <Section title="Core Information">
                  <label style={fieldLabel}>
                    <span style={labelText}>Category</span>
                    <Select
                      disabled={!canSelectCategory && !canCreateCategories}
                      error={validationState.errors.category?.message}
                      style={inputStyle}
                      {...categoryRegister}
                    >
                      <option value="">Select...</option>
                      {activeCategories.map((category) => (
                        <option key={category.id || category.name} value={category.name}>
                          {category.name}
                        </option>
                      ))}
                      {canCreateCategories && (
                        <option value={ADD_NEW_CATEGORY_OPTION_VALUE}>+ Add New Category</option>
                      )}
                    </Select>
                    {!canSelectCategory && !validationState.errors.category?.message && (
                      <span style={helperText}>
                        {canCreateCategories
                          ? "Create and select a category without leaving this form."
                          : "No active categories are available. Ask an administrator to configure them."}
                      </span>
                    )}
                  </label>

                  {showCreateCategory && canCreateCategories && (
                    <div style={inlineCategoryCard}>
                      <div style={{ fontSize: typography.size.sm, fontWeight: typography.weight.medium }}>
                        Add New Category
                      </div>
                      <Input
                        label="Category name"
                        value={newCategoryName}
                        maxLength={RISK_CATEGORY_NAME_MAX_LENGTH}
                        onChange={(event) => {
                          setNewCategoryName(event.target.value);
                          if (newCategoryError) {
                            setNewCategoryError("");
                          }
                        }}
                        placeholder="e.g., Information Security"
                      />
                      <Input
                        label="Description"
                        value={newCategoryDescription}
                        onChange={(event) => setNewCategoryDescription(event.target.value)}
                        placeholder="Optional description"
                      />
                      {newCategoryError && (
                        <div style={inlineErrorStyle} role="alert">
                          {newCategoryError}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: spacing.sm }}>
                        <Button
                          type="button"
                          variant="primary"
                          onClick={() => handleCreateCategory(setValue)}
                          disabled={creatingCategory}
                        >
                          {creatingCategory ? "Creating..." : "Create category"}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setShowCreateCategory(false);
                            setNewCategoryName("");
                            setNewCategoryDescription("");
                            setNewCategoryError("");
                          }}
                          disabled={creatingCategory}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  <Field
                    label="Subcategory"
                    name="subcategory"
                    type="select"
                    register={register}
                    disabled={!subcategories.length}
                    options={subcategories}
                  />

                  <label style={fieldLabel}>
                    <span style={labelText}>Risk Name</span>
                    <Input error={validationState.errors.name?.message} style={inputStyle} {...nameRegister} />
                  </label>

                  <Field label="Risk Type" name="risk_type" register={register} />

                  <label style={fieldLabel}>
                    <span style={labelText}>Owner</span>
                    <Select
                      disabled={ownersLoading || ownerOptions.length === 0}
                      style={inputStyle}
                      {...ownerRegister}
                    >
                      <option value="">Select...</option>
                      {ownerOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Select>
                    {ownersLoading && <span style={helperText}>Loading users...</span>}
                    {!ownersLoading && ownersError && ownerOptions.length === 0 && (
                      <span style={helperText}>{ownersError}</span>
                    )}
                  </label>

                  <label style={fieldLabel}>
                    <span style={labelText}>Editor</span>
                    <Select
                      disabled={ownersLoading || ownerOptions.length === 0}
                      style={inputStyle}
                      {...editorRegister}
                    >
                      <option value="">Select...</option>
                      {ownerOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </Select>
                    {ownersLoading && <span style={helperText}>Loading users...</span>}
                    {!ownersLoading && ownersError && ownerOptions.length === 0 && (
                      <span style={helperText}>{ownersError}</span>
                    )}
                  </label>
                </Section>
                <Section title="Risk Matrix">
                  <input type="hidden" {...register("likelihood")} />
                  <input type="hidden" {...register("impact")} />
                  <input type="hidden" {...register("matrix_id")} />

                  <label style={fieldLabel}>
                    <span style={labelText}>Impact Theme</span>
                    <Select
                      aria-label="Impact Theme"
                      value={selectedImpactThemeId ?? ""}
                      onChange={(event) => setters.setImpactThemeId(toOptionalNumber(event.target.value))}
                      disabled={impactThemesLoading || impactThemes.length === 0}
                      style={inputStyle}
                      error={impactThemesError || impactThemeLevelsError ? " " : undefined}
                    >
                      <option value="">Select...</option>
                      {impactThemes.map((theme) => (
                        <option key={theme.id} value={theme.id}>{theme.name}</option>
                      ))}
                    </Select>
                    {impactThemesLoading && <span style={helperText}>Loading impact themes...</span>}
                    {!impactThemesLoading && impactThemesError && <span style={helperText}>{impactThemesError}</span>}
                    {!impactThemesLoading && !impactThemesError && impactThemeLevelsLoading && (
                      <span style={helperText}>Loading impact definitions...</span>
                    )}
                    {!impactThemesLoading && !impactThemesError && impactThemeLevelsError && (
                      <span style={helperText}>{impactThemeLevelsError}</span>
                    )}
                  </label>

                  <div style={matrixContainerStyle}>
                    <div style={matrixGridWrapperStyle}>
                      <RiskMatrixSelector
                        likelihoodLevels={ratingDefinitions?.likelihood || []}
                        impactLevels={impactThemeLevels}
                        selectedLikelihood={likelihoodValue}
                        selectedImpact={impactValue}
                        onSelect={({ likelihood, impact }) => {
                          setValue("likelihood", likelihood, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                          setValue("impact", impact, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
                          setters.setLikelihood(likelihood);
                          setters.setImpact(impact);
                        }}
                        impactTheme={selectedImpactTheme}
                        matrixConfig={matrixConfig}
                        readOnly={false}
                        cellAriaLabelFormatter={({ likelihood, impact, score }) =>
                          `Select Likelihood ${likelihood} and Impact ${impact}. Score ${score}.`
                        }
                      />
                      {matrixConfigLoading && <div style={helperText}>Loading matrix configuration...</div>}
                      {!matrixConfigLoading && matrixConfigError && (
                        <div style={inlineErrorStyle} role="alert">{matrixConfigError}</div>
                      )}
                      {ratingDefinitionsLoading && <div style={helperText}>Loading rating definitions...</div>}
                      {!ratingDefinitionsLoading && ratingDefinitionsError && (
                        <div style={inlineErrorStyle} role="alert">{ratingDefinitionsError}</div>
                      )}
                    </div>

                    <div style={matrixDetailPanelStyle}>
                      <div style={{ fontSize: typography.size.sm, fontWeight: typography.weight.medium }}>Rating Details</div>
                      {!hasSelectedCell && (
                        <div style={helperText}>No score selected yet. Choose a matrix square.</div>
                      )}
                      {hasSelectedCell && (
                        <div style={{ display: "grid", gap: spacing.xs }}>
                          <div style={matrixDetailBlockStyle}>
                            <div style={matrixDetailHeadingStyle}>Likelihood:</div>
                            <div style={matrixDetailLineStyle}>
                              Level {likelihoodValue} - {selectedLikelihoodDefinition?.label || `Level ${likelihoodValue}`}
                            </div>
                            <div style={detailTextStyle}>
                              Definition: {selectedLikelihoodDefinition?.definition || "Definition unavailable."}
                            </div>
                          </div>
                          <div style={matrixDetailBlockStyle}>
                            <div style={matrixDetailHeadingStyle}>Impact ({selectedImpactThemeName}):</div>
                            <div style={matrixDetailLineStyle}>
                              Level {impactValue} - {selectedImpactDefinition?.label || `Level ${impactValue}`}
                            </div>
                            <div style={detailTextStyle}>
                              Definition: {selectedImpactDefinition?.definition || "Definition unavailable."}
                            </div>
                          </div>
                          <div style={readonlyValue}>Calculated Score: {selectedScore}</div>
                          <div style={readonlyValue}>Risk Band: {selectedBand}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  <ReadOnlyItem label="Risk Score" value={hasSelectedCell ? selectedScore : "-"} />
                  <div style={helperText}>Matrix selection is optional. If selected, the score and band are captured.</div>
                </Section>

                <CollapsibleSection title="Additional Details" defaultOpen={false}>
                  <RichTextField
                    label="Description"
                    name="description"
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    onValueChange={setters.setDescription}
                  />
                  <RichTextField
                    label="Impacts / Implications"
                    name="impact_implications"
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    onValueChange={setters.setImpactImplications}
                  />
                  <RichTextField
                    label="Causes"
                    name="causes"
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    onValueChange={setters.setCauses}
                  />
                </CollapsibleSection>
              </div>
            );
          }}
        </FormWithValidation>
      </div>
    </ModalA11yWrapper>
      )}
    </UnsavedChangesBoundary>
  );
}

function Section({ title, children }) {
  return (
    <div style={sectionStyle}>
      <h3 style={sectionTitle}>{title}</h3>
      <div style={{ display: "grid", gap: spacing.xl }}>{children}</div>
    </div>
  );
}

function Field({ label, name, register, type = "text", options = [], disabled = false, error, helper }) {
  return (
    <label style={fieldLabel}>
      <span style={labelText}>{label}</span>
      {type === "select" ? (
        <Select disabled={disabled} error={error} style={inputStyle} {...register(name)}>
          <option value="">Select...</option>
          {options.map((opt) => {
            const value = typeof opt === "object" ? opt.value : opt;
            const optionLabel = typeof opt === "object" ? opt.label : opt;
            return (
              <option key={value} value={value}>
                {optionLabel}
              </option>
            );
          })}
        </Select>
      ) : (
        <Input type={type} error={error} style={inputStyle} {...register(name)} />
      )}
      {helper && !error && <span style={helperText}>{helper}</span>}
    </label>
  );
}

function RichTextField({ label, name, register, watch, setValue, onValueChange }) {
  const value = watch(name) || "";

  return (
    <div style={fieldLabel}>
      <span style={labelText}>{label}</span>
      <input type="hidden" {...register(name)} />
      <RichTextEditor
        value={value}
        onChange={(nextValue) => {
          setValue(name, nextValue || "", {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
          });
          onValueChange?.(nextValue || "");
        }}
        minHeight={100}
      />
    </div>
  );
}

function CategoryWatcher({ categories, setSubcategories, watch, setValue }) {
  const categoryValue = watch("category");

  useEffect(() => {
    if (!categoryValue) {
      setSubcategories([]);
      return;
    }
    const selected = categories.find((entry) => entry.name === categoryValue);
    setSubcategories(selected?.subcategories || []);
    setValue("subcategory", "");
  }, [categoryValue, categories, setSubcategories, setValue]);

  return null;
}

function ReadOnlyItem({ label, value }) {
  return (
    <div>
      <div style={readonlyLabel}>{label}</div>
      <div style={readonlyValue}>{value}</div>
    </div>
  );
}

const cardStyle = {
  width: 780,
  maxWidth: "95vw",
  maxHeight: "90vh",
  overflowY: "auto",
  background: "#f9fafb",
  borderRadius: 14,
  boxShadow: "0 0 30px rgba(0,0,0,0.3)",
  display: "flex",
  flexDirection: "column",
};

const headerStyle = {
  padding: `${spacing.md * 2}px ${spacing.lg * 2}px`,
  borderBottom: "1px solid rgba(148,163,184,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "white",
};

const closeStyle = {
  border: "none",
  background: "transparent",
  fontSize: typography.size.xl,
  color: "#6b7280",
  cursor: "pointer",
};

const formStyle = {
  padding: `${spacing.lg * 2}px ${spacing.xl + spacing.lg}px ${spacing.xl * 2}px`,
  display: "flex",
  flexDirection: "column",
  gap: spacing.xl + spacing.sm,
};

const sectionStyle = {
  padding: spacing.md + spacing.sm,
  borderRadius: 12,
  background: "white",
  border: "1px solid rgba(148,163,184,0.35)",
  boxShadow: "0 8px 14px rgba(15,23,42,0.05)",
};

const sectionTitle = {
  margin: `0 0 ${spacing.xl}px`,
  fontSize: typography.size.lg,
  color: "#111827",
};

const fieldLabel = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
};

const labelText = {
  fontSize: typography.size.sm,
  color: "#374151",
};

const inputStyle = {
  width: "100%",
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.base,
  marginBottom: spacing.md,
  borderRadius: "4px",
  border: "1px solid #ccc",
  boxSizing: "border-box",
};

const readonlyLabel = {
  fontWeight: typography.weight.medium,
  marginBottom: spacing.xs,
  fontSize: typography.size.sm,
};

const readonlyValue = {
  padding: spacing.md,
  background: "#f6f6f6",
  borderRadius: "4px",
  fontSize: typography.size.sm,
  border: "1px solid #ddd",
};

const helperText = {
  fontSize: typography.size.xs,
  color: "#6b7280",
};

const infoBannerStyle = {
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderRadius: 10,
  border: "1px solid #bfdbfe",
  background: "#eff6ff",
  color: "#1e3a8a",
  fontSize: typography.size.xs,
};

const alertStyle = {
  padding: spacing.md,
  borderRadius: 10,
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fed7aa",
  display: "grid",
  gap: spacing.xs,
};

const inlineCategoryCard = {
  padding: spacing.md,
  borderRadius: 10,
  border: "1px solid #d1d5db",
  background: "#f8fafc",
  display: "grid",
  gap: spacing.sm,
};

const inlineErrorStyle = {
  padding: spacing.sm,
  borderRadius: 8,
  border: "1px solid #fecaca",
  background: "#fef2f2",
  color: "#b91c1c",
  fontSize: typography.size.sm,
};

const matrixContainerStyle = {
  display: "grid",
  gap: spacing.md,
  gridTemplateColumns: "minmax(320px, 1fr) minmax(240px, 320px)",
};

const matrixGridWrapperStyle = {
  display: "grid",
  gap: spacing.xs,
};

const matrixDetailPanelStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: spacing.md,
  background: "#ffffff",
  display: "grid",
  gap: spacing.sm,
  alignContent: "start",
};

const matrixDetailBlockStyle = {
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  background: "#ffffff",
  padding: spacing.sm,
  display: "grid",
  gap: 4,
};

const matrixDetailHeadingStyle = {
  fontWeight: typography.weight.medium,
  fontSize: typography.size.sm,
  color: "#0f172a",
};

const matrixDetailLineStyle = {
  fontSize: typography.size.sm,
  color: "#334155",
};

const detailTextStyle = {
  marginTop: 0,
  fontSize: typography.size.xs,
  color: "#374151",
  whiteSpace: "pre-wrap",
};
